import { useCardsRouterContext } from '@apps/card/Card.routes';
import { getDimensions, getOrientation } from '@apps/card/utils';
import { ChoiceGroup, useCustomizationContext } from '@apps/card/routes/CardCustomizer';
import React from 'react';
import { useTemplatesBySize } from './OrientationChoiceGroup';
import { formatCardSizeName, useCardSizeLabels } from '@apps/card/utils/cardSizeName';
import { useTranslation } from '@shared/core';
import { useCardTelemetry } from '@apps/card/Card.telemetry';

export const SizeChoiceGroup = (props: { templateId: string }) => {
  const { templateId } = props;
  const { tq } = useTranslation('stationery');
  const sizeLabels = useCardSizeLabels();
  const { buttonInteracted } = useCardTelemetry({ page: 'productPdpModal' });
  const [draft] = useCustomizationContext(draft => draft);
  const category = draft.stationeryTemplateCategory;
  const dimensions = getDimensions(draft.config.card);
  const orientation = getOrientation(dimensions.width, dimensions.height);

  const templates = useTemplatesBySize(category, draft.template.themeId).filter(t => {
    return orientation === getOrientation(t.widthInches, t.heightInches);
  });

  const { goToProductDetailsPage } = useCardsRouterContext();

  if (!templates.length) return null;
  return (
    <ChoiceGroup
      title={tq`Size`}
      value={templateId}
      setValue={nextTemplateId => {
        const nextTemplate = templates.find(t => t.id === nextTemplateId);
        buttonInteracted({
          name: 'pdpModal_sizeControlClicked',
          themeId: draft.template.themeId,
          stationeryTemplateCategory: category,
          fromTemplateId: templateId,
          toTemplateId: nextTemplateId,
          fromCardWidthInches: dimensions.width,
          fromCardHeightInches: dimensions.height,
          toCardWidthInches: nextTemplate?.widthInches,
          toCardHeightInches: nextTemplate?.heightInches,
        });
        goToProductDetailsPage('wedding', category, draft.template.themeId, nextTemplateId, 'replace');
      }}
      choices={templates.map(t => t.id)}
      getName={(id: string) => {
        // The current template can be missing from the enabled-only size list
        // (e.g. opened via direct link while still disabled) — label it from the
        // card's own dimensions instead of crashing.
        const template = templates.find(t => t.id === id);
        const width = template?.widthInches ?? dimensions.width;
        const height = template?.heightInches ?? dimensions.height;
        return formatCardSizeName(category, width, height, sizeLabels);
      }}
      textTransform="none" // keep "inches" lowercase
      minHeight={55}
    />
  );
};

export const ReadOnlySizeChoiceGroup = () => {
  const { tq } = useTranslation('stationery');
  const [cardDimensions] = useCustomizationContext(draft => getDimensions(draft.config.card));
  const [category] = useCustomizationContext(draft => draft.stationeryTemplateCategory);
  const sizeLabels = useCardSizeLabels();
  const cardSizeText = formatCardSizeName(category, cardDimensions.width, cardDimensions.height, sizeLabels);

  return (
    <ChoiceGroup
      title={tq`Size`}
      value={cardSizeText}
      textTransform="none"
    />
  );
};
